import { Config } from "@config";
import { faXmark as fasXmark } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { logError } from "@utils/sentry-error";
import autobind from "autobind-decorator";
import { http, Button, FormEmail, FormInput, ProgressButton, PromisePool } from "bm-ui-kit";
import type { ProgressButtonState } from "bm-ui-kit";
import type { ReactNode } from "react";
import React from "react";
import { Form } from "react-bootstrap";
import { Form as FinalForm } from "react-final-form";
import type { SubscriptionTypes } from "../NewsletterSubscribe/NewsletterSubscribe";

interface Props {
	subscriptionTypes: SubscriptionTypes[];
	onClosed: () => void;
	onSubscribe: (email: string) => void;
}

interface State {
	buttonState: ProgressButtonState;
	messageState: "subscribe" | "thank" | "close";
}

interface FormFields {
	email: string;
	name: string;
}

export class ArticleFooterNewsletterSubscribe extends React.Component<Props, State> {
	private readonly promisePool = new PromisePool();

	public constructor(props: Props) {
		super(props);

		this.state = {
			buttonState: undefined,
			messageState: "subscribe",
		};
	}

	private getInitialValues(): FormFields {
		return {
			email: Config.getCurrentUser().getEmail(),
			name: Config.getCurrentUser().getFullName(),
		};
	}

	public componentWillUnmount(): void {
		this.promisePool.cancel();
	}

	public render(): ReactNode {
		const { onClosed, subscriptionTypes } = this.props;
		const { messageState, buttonState } = this.state;

		switch (messageState) {
			case "subscribe":
				return (
					<FinalForm<FormFields> initialValues={this.getInitialValues()} onSubmit={this.onSubmit}>
						{({ handleSubmit, form }) => (
							<Form
								onSubmit={handleSubmit}
								className="animated fadeInUp faster fixed-bottom bg-dark text-white shadow py-2"
							>
								<div className="d-lg-flex d-none justify-content-center align-items-center container">
									<div>
										{subscriptionTypes.includes("CORPORATE_NEWSLETTER")
											? "Iratkozz fel hírlevelünkre, hogy ne maradj le vállalkozásod új lehetőségeiről és a fontos hírekről!"
											: "Értesülj elsőkézből a legújabb pénzügyi hírekről és ajánlatokról!"}

										<br />
										<small>
											Feliratkozom a hírlevélre és elfogadom az{" "}
											<a href="/adatvedelem/">adatkezelési tájékoztatót</a>.
										</small>
									</div>
									<div className="d-flex ml-2">
										<FormInput
											name="name"
											placeholder="Keresztnév"
											className="mb-0"
											validate={this.validName}
											required
										/>
										<FormEmail
											name="email"
											placeholder="E-mail cím"
											className="mb-0 mx-2"
											prepend=""
											required
										/>
										<ProgressButton
											className="py-0"
											type="submit"
											size="sm"
											state={buttonState}
											onClick={form.submit}
										>
											Feliratkozom
										</ProgressButton>
									</div>
									<Button
										size="sm"
										variant="light"
										icon={<FontAwesomeIcon icon={fasXmark} />}
										className="ml-2"
										outlined
										aria-label="Bezárás"
										onClick={onClosed}
									/>
								</div>
							</Form>
						)}
					</FinalForm>
				);
			case "thank":
				return (
					<div className="animated fadeInUp faster fixed-bottom bg-success shadow py-2">
						<div className="d-lg-flex d-none justify-content-center align-items-center container">
							<div className="text-white">
								<div className="h3 mb-0">Sikeresen feliratkoztál!</div>
								<div className="small">
									Minden kedden számíts a hét a legjobb híreire, tanácsaira és legújabb ajánlataira.
									Addig is, ha többre vágysz, kövess minket{" "}
									<a
										className="text-reset"
										href="https://www.facebook.com/Bankmonitor"
										target="blank"
									>
										Facebookon
									</a>
									!
								</div>
							</div>
							<Button
								size="sm"
								variant="light"
								icon={<FontAwesomeIcon icon={fasXmark} />}
								className="ml-auto"
								outlined
								aria-label="Bezárás"
								onClick={this.onMessageClose}
							/>
						</div>
					</div>
				);
			case "close":
			default:
				return null;
		}
	}

	private validName(name: string): string {
		if (typeof name !== "string" || name.length < 1) {
			return "A név megadása kötelező!";
		}
	}

	@autobind
	private onMessageClose(): void {
		const { onClosed } = this.props;
		this.setState({
			messageState: "close",
		});

		if (onClosed) {
			onClosed();
		}
	}

	@autobind
	private onSubmit(values: FormFields): void {
		const { onSubscribe, subscriptionTypes } = this.props;

		this.setState({
			buttonState: "loading",
		});
		// Feliratkozás
		this.promisePool
			.add(
				http.post(Config.getWpRestApiUrl(`/subscribe/${encodeURIComponent(values.email)}`), {
					subscriptionTypes,
					firstName: values.name,
				}),
			)
			.then(() => {
				if (onSubscribe) {
					onSubscribe(values.email);
				}
				this.promisePool.addTimeout(7_000).then(() => {
					this.setState({
						messageState: "close",
					});
				});

				this.setState({
					buttonState: "done",
					messageState: "thank",
				});
			})
			.catch((error: Error) => {
				this.setState({
					buttonState: "error",
				});

				logError(error);
			});
	}
}
